/**
 * Shared behaviour for anything that opens over the page.
 *
 * Three separate hand-rolled copies of "Escape closes, an outside click
 * closes, the body stops scrolling" had drifted apart, and none of them moved
 * focus — so a screen-reader or keyboard user opening the detail drawer stayed
 * parked behind it on the table, tabbing through content they could not see.
 */
import { useEffect, useRef, type RefObject } from 'react'

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function focusableWithin(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (element) => element.offsetParent !== null || element === document.activeElement,
  )
}

export type DismissReason = 'escape' | 'outside'

export interface DismissableOptions {
  open: boolean
  /** `reason` says which gesture closed it; see `DismissReason`. */
  onClose: (reason: DismissReason) => void
  /** Close when a pointer goes down outside the element. Menus want this. */
  closeOnOutsideClick?: boolean
  /** Hold focus inside and restore it on close. Dialogs want this. */
  trapFocus?: boolean
  /** Stop the page behind from scrolling. Dialogs want this. */
  lockScroll?: boolean
}

/**
 * Wire up an overlay. Returns the ref to attach to its root element.
 */
export function useDismissable<T extends HTMLElement>({
  open,
  onClose,
  closeOnOutsideClick = false,
  trapFocus = false,
  lockScroll = false,
}: DismissableOptions): RefObject<T | null> {
  const ref = useRef<T>(null)
  // Held in a ref so a re-rendered `onClose` does not tear down the listeners
  // (and, with them, the remembered focus) mid-interaction.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return

    const previouslyFocused = document.activeElement as HTMLElement | null

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onCloseRef.current('escape')
        return
      }
      if (event.key !== 'Tab' || !trapFocus || !ref.current) return

      const items = focusableWithin(ref.current)
      if (items.length === 0) {
        event.preventDefault()
        ref.current.focus()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      const active = document.activeElement

      if (event.shiftKey && (active === first || !ref.current.contains(active))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    const onPointerDown = (event: MouseEvent) => {
      if (!closeOnOutsideClick) return
      if (!ref.current?.contains(event.target as Node)) onCloseRef.current('outside')
    }

    document.addEventListener('keydown', onKeyDown, true)
    document.addEventListener('mousedown', onPointerDown)

    let previousOverflow = ''
    if (lockScroll) {
      previousOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    }

    // Move focus in on the next frame: the element is not laid out yet on the
    // tick that opened it, so `offsetParent` filtering would reject everything.
    const frame = requestAnimationFrame(() => {
      if (!trapFocus || !ref.current) return
      if (ref.current.contains(document.activeElement)) return
      const items = focusableWithin(ref.current)
      ;(items[0] ?? ref.current).focus()
    })

    return () => {
      cancelAnimationFrame(frame)
      document.removeEventListener('keydown', onKeyDown, true)
      document.removeEventListener('mousedown', onPointerDown)
      if (lockScroll) document.body.style.overflow = previousOverflow
      // Put the caret back where it came from, but only if focus is still
      // somewhere in the overlay; if the user has already clicked elsewhere,
      // yanking it back would be the surprise.
      if (trapFocus && previouslyFocused?.isConnected) {
        const active = document.activeElement
        if (!active || active === document.body || ref.current?.contains(active)) {
          previouslyFocused.focus()
        }
      }
    }
  }, [open, closeOnOutsideClick, trapFocus, lockScroll])

  return ref
}
